import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TPageProps } from 'models/common.model'
import { useNextStep } from 'hooks/useNextStep'
import { Option, OptionType } from 'components/Option'
import { AnswerButtonLight } from 'components/AnswerButtonLight'
import { StyledFacedWithFeelings as S } from './FacedWithFeelings.styles'
import { OPTION_VALUES, QUESTION } from './constants'

export const FacedWithFeelings: React.FC<TPageProps> = ({
  pageId,
  nextPagePath,
}) => {
  const { t } = useTranslation()
  const [answers, setAnswers] = useState<string[]>([])

  const handleContinue = useNextStep({
    pageId,
    question: QUESTION,
    nextPagePath,
  })

  const optionProps = useMemo(
    () => ({
      name: pageId,
      type: OptionType.CHECKBOX,
      onChange: (value: string, isChecked: boolean) => {
        if (isChecked) {
          setAnswers([...answers, value])
        } else {
          setAnswers(answers.filter((item) => item !== value))
        }
      },
    }),
    [pageId, answers],
  )

  const handleClick = useCallback(
    () => handleContinue(answers),
    [handleContinue, answers],
  )

  return (
    <S.Wrapper>
      <S.Title>{t('onboarding.facedWithFeelings.title')}</S.Title>
      <S.Subtitle>{t('onboarding.facedWithFeelings.subtitle')}</S.Subtitle>

      <Option
        {...optionProps}
        value={OPTION_VALUES.LACK_OF_ENERGY}
        checked={answers.includes(OPTION_VALUES.LACK_OF_ENERGY)}
      >
        <AnswerButtonLight>
          {t('onboarding.facedWithFeelings.options.lackOfEnergy')}
        </AnswerButtonLight>
      </Option>

      <Option
        {...optionProps}
        value={OPTION_VALUES.LITTLE_INTEREST}
        checked={answers.includes(OPTION_VALUES.LITTLE_INTEREST)}
      >
        <AnswerButtonLight>
          {t('onboarding.facedWithFeelings.options.littleInterest')}
        </AnswerButtonLight>
      </Option>

      <Option
        {...optionProps}
        value={OPTION_VALUES.TROUBLE_WITH_CONCENTRATION}
        checked={answers.includes(OPTION_VALUES.TROUBLE_WITH_CONCENTRATION)}
      >
        <AnswerButtonLight>
          {t('onboarding.facedWithFeelings.options.troubleWithConcentration')}
        </AnswerButtonLight>
      </Option>

      <Option
        {...optionProps}
        value={OPTION_VALUES.INCREASED_IRRITABILITY}
        checked={answers.includes(OPTION_VALUES.INCREASED_IRRITABILITY)}
      >
        <AnswerButtonLight>
          {t('onboarding.facedWithFeelings.options.increasedIrritability')}
        </AnswerButtonLight>
      </Option>

      <S.Button type="button" disabled={!answers.length} onClick={handleClick}>
        {t`actions.continue`}
      </S.Button>
    </S.Wrapper>
  )
}
